import * as React from "react";

export const longPressDurationMs = 2000;

export function useLongPress(onLongPress?: () => void) {
    const timerRef = React.useRef<number | undefined>(undefined);
    const longPressTriggeredRef = React.useRef(false);
    const suppressClickRef = React.useRef(false);
    const [isHolding, setIsHolding] = React.useState(false);

    const clearTimer = React.useCallback(() => {
        if (timerRef.current !== undefined) {
            window.clearTimeout(timerRef.current);
            timerRef.current = undefined;
        }
    }, []);

    const cancel = React.useCallback((suppressClick = false) => {
        clearTimer();
        setIsHolding(false);
        if (suppressClick) {
            suppressClickRef.current = true;
        }
    }, [clearTimer]);

    const start = React.useCallback(() => {
        cancel();
        longPressTriggeredRef.current = false;
        suppressClickRef.current = false;
        if (!onLongPress) {
            return;
        }
        setIsHolding(true);
        timerRef.current = window.setTimeout(() => {
            timerRef.current = undefined;
            longPressTriggeredRef.current = true;
            onLongPress();
        }, longPressDurationMs);
    }, [cancel, onLongPress]);

    const consumeClick = React.useCallback(() => {
        const wasConsumed = longPressTriggeredRef.current || suppressClickRef.current;
        longPressTriggeredRef.current = false;
        suppressClickRef.current = false;
        return wasConsumed;
    }, []);

    React.useEffect(() => {
        const cancelOnBlur = () => cancel(true);
        const cancelWhenHidden = () => {
            if (document.hidden) {
                cancel(true);
            }
        };
        window.addEventListener("blur", cancelOnBlur);
        document.addEventListener("visibilitychange", cancelWhenHidden);
        return () => {
            clearTimer();
            window.removeEventListener("blur", cancelOnBlur);
            document.removeEventListener("visibilitychange", cancelWhenHidden);
        };
    }, [cancel, clearTimer]);

    return { start, cancel, consumeClick, isHolding };
}
